import type { MessageInitShape, MessageShape } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useMutation } from '@connectrpc/connect-query'
import {
  applyBiddingBlockCsv,
  checkBiddingBlockCsv,
} from '@ebalance/proto/bffuser/v1/bidding_block_service-BiddingBlockService_connectquery'
import type {
  ApplyBiddingBlockCsvRequestSchema,
  ApplyBiddingBlockCsvResponseSchema,
  CheckBiddingBlockCsvRequestSchema,
  CheckBiddingBlockCsvResponseSchema,
} from '@ebalance/proto/bffuser/v1/bidding_block_service_pb'
import {
  applyBiddingSpotNormalCsv,
  checkBiddingSpotNormalCsv,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service-BiddingSpotService_connectquery'
import type {
  ApplyBiddingSpotNormalCsvRequestSchema,
  ApplyBiddingSpotNormalCsvResponseSchema,
  CheckBiddingSpotNormalCsvRequestSchema,
  CheckBiddingSpotNormalCsvResponseSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_service_pb'
import type { UseMutationResult } from '@tanstack/react-query'

import { useWorkflowCaseScopedMutation } from '~/hooks/bidding/useWorkflowCaseScopedMutation'

/**
 * 通常入札 CSV の Check mutation (docs/specs/bidding/spot-bid-normal-csv-import.md
 * AC-SPOT-001〜007)。副作用なし: 行ごとのプレビューと check_token を返すだけで
 * 札は 1 件も作られない (common.md §3 の 2 段階分離の前段)。
 */
export function useCheckSpotNormalCsv(): UseMutationResult<
  MessageShape<typeof CheckBiddingSpotNormalCsvResponseSchema>,
  ConnectError,
  MessageInitShape<typeof CheckBiddingSpotNormalCsvRequestSchema>
> {
  return useMutation(checkBiddingSpotNormalCsv)
}

/** 通常入札 CSV の Apply mutation (= check_token を引き換えて承認依頼を作成)。 */
export function useApplySpotNormalCsv(): UseMutationResult<
  MessageShape<typeof ApplyBiddingSpotNormalCsvResponseSchema>,
  ConnectError,
  MessageInitShape<typeof ApplyBiddingSpotNormalCsvRequestSchema>
> {
  return useWorkflowCaseScopedMutation(useMutation(applyBiddingSpotNormalCsv))
}

/**
 * ブロック入札 CSV の Check mutation (docs/specs/bidding/spot-bid-block-csv-import.md
 * AC-DSB-001〜012)。通常入札と同じく副作用なし。
 */
export function useCheckBlockCsv(): UseMutationResult<
  MessageShape<typeof CheckBiddingBlockCsvResponseSchema>,
  ConnectError,
  MessageInitShape<typeof CheckBiddingBlockCsvRequestSchema>
> {
  return useMutation(checkBiddingBlockCsv)
}

/** ブロック入札 CSV の Apply mutation (= check_token を引き換えてグループを作成)。 */
export function useApplyBlockCsv(): UseMutationResult<
  MessageShape<typeof ApplyBiddingBlockCsvResponseSchema>,
  ConnectError,
  MessageInitShape<typeof ApplyBiddingBlockCsvRequestSchema>
> {
  return useWorkflowCaseScopedMutation(useMutation(applyBiddingBlockCsv))
}
